import { useEffect } from "react";
import { FadeUp } from "@/components/Reveal";

const Block = ({ title, children }) => (
  <div className="mt-12">
    <h2 className="font-serif text-2xl text-gold">{title}</h2>
    <div className="mt-4 space-y-3 text-sm leading-relaxed text-cream/60">
      {children}
    </div>
  </div>
);

const LegalLayout = ({ title, updated, testid, children }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <main data-testid={testid} className="pb-28 pt-40">
      <div className="mx-auto max-w-3xl px-6 lg:px-10">
        <FadeUp y={20}>
          <div className="flex items-center gap-4">
            <span className="chapter-line" />
            <span className="text-xs font-semibold uppercase tracking-[0.35em] text-gold">
              Legal
            </span>
          </div>
          <h1 className="mt-8 font-serif text-4xl tracking-[-0.02em] text-cream sm:text-5xl">
            {title}
          </h1>
          <p className="mt-5 text-xs uppercase tracking-[0.2em] text-cream/40">
            {updated}
          </p>
        </FadeUp>
        <FadeUp delay={0.15}>{children}</FadeUp>
      </div>
    </main>
  );
};

export function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      updated="Last updated: August 2026"
      testid="privacy-page"
    >
      <Block title="Overview">
        <p>
          MK Group Plus LLC ("we", "our", "us") is a digital marketing agency
          located at Shams Business Center, Sharjah, United Arab Emirates. This
          policy explains what information we collect through this website and
          how we use it.
        </p>
      </Block>
      <Block title="Information We Collect">
        <p>
          When you submit our consultation or contact form, we collect your
          name, email address, phone number (optional), the service you are
          interested in, and your message. We do not collect payment
          information through this website.
        </p>
      </Block>
      <Block title="How We Use Your Information">
        <p>
          We use your details solely to respond to your enquiry, prepare
          proposals, and communicate with you about our services. We do not
          sell, rent, or share your personal information with third parties for
          their marketing purposes.
        </p>
      </Block>
      <Block title="Third-Party Services">
        <p>
          Enquiry notifications are delivered to us through a secure email
          delivery provider. Our hosting infrastructure may process standard
          technical logs (such as IP address and browser type) for security and
          performance purposes only.
        </p>
      </Block>
      <Block title="Data Retention & Security">
        <p>
          Enquiry records are retained only for as long as needed to manage our
          business relationship with you and are protected with reasonable
          technical safeguards.
        </p>
      </Block>
      <Block title="Your Rights">
        <p>
          You may request access to, correction of, or deletion of your
          personal data at any time by emailing office@mkplus.llc.
        </p>
      </Block>
      <Block title="Changes to This Policy">
        <p>
          We may update this policy from time to time. The current version will
          always be published on this page.
        </p>
      </Block>
      <Block title="Contact">
        <p>
          MK Group Plus LLC — Shams Business Center, Sharjah, United Arab
          Emirates — office@mkplus.llc
        </p>
      </Block>
    </LegalLayout>
  );
}

export function TermsConditions() {
  return (
    <LegalLayout
      title="Terms & Conditions"
      updated="Last updated: August 2026"
      testid="terms-page"
    >
      <Block title="Services">
        <p>
          MK Group Plus LLC provides digital marketing services including
          social media management, search engine optimization (SEO), Google
          Ads, Meta Ads, content creation, and website design. The exact scope
          of any engagement is defined in a written proposal or agreement.
        </p>
      </Block>
      <Block title="Quotes & Pricing">
        <p>
          Every project is quoted individually based on scope, channels, and
          duration. Before any work begins, you will receive a written proposal
          outlining deliverables, timelines, and pricing. Quotes are provided
          free of charge and without obligation.
        </p>
      </Block>
      <Block title="Client Responsibilities">
        <p>
          Clients agree to provide timely access, approvals, and materials
          reasonably required to deliver the services. Advertising spend on
          platforms such as Google and Meta is paid by the client directly to
          those platforms unless agreed otherwise in writing.
        </p>
      </Block>
      <Block title="Results">
        <p>
          We design every campaign around measurable growth; however, we do not
          guarantee specific rankings, lead volumes, or revenue outcomes, as
          these depend on factors beyond our control, including market
          conditions and platform policies.
        </p>
      </Block>
      <Block title="Intellectual Property">
        <p>
          Upon full payment, deliverables created specifically for the client
          belong to the client. We retain the right to reference completed work
          in our portfolio unless agreed otherwise in writing.
        </p>
      </Block>
      <Block title="Confidentiality">
        <p>
          Both parties agree to keep confidential any non-public business
          information shared during the engagement.
        </p>
      </Block>
      <Block title="Limitation of Liability">
        <p>
          To the maximum extent permitted by law, our liability for any claim
          arising from our services is limited to the fees paid for the
          relevant service period.
        </p>
      </Block>
      <Block title="Governing Law">
        <p>
          These terms are governed by the laws of the United Arab Emirates. Any
          disputes shall be subject to the jurisdiction of the courts of the
          Emirate of Sharjah.
        </p>
      </Block>
      <Block title="Contact">
        <p>
          MK Group Plus LLC — Shams Business Center, Sharjah, United Arab
          Emirates — office@mkplus.llc
        </p>
      </Block>
    </LegalLayout>
  );
}
